import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import { fmtDateTime } from '../../../utils/region';
import Pagination, { getPageSize } from '../../Pagination';
import type { AwExpensePaymentPage, AwExpensePaymentRow } from '../../../types';
import type { AwCtx } from '../ctx';
import { Loader, emptyLine } from '../dialog';
import { FigureLine, NoCell, NoNumber, STRUCK, SmallLine, StatusCell } from '../listCells';
import { SortTh, sortArgs, useListSort } from '../listSort';
import { useOpenRecord } from '../relatedDocs';
import { AttachmentsBtn, useAttachmentCounts } from '../attachments';   // owner 2026-11-02: a payment's supporting files
import RowMenu from '../RowMenu';
import { amt, card, dmy, errBox, inp, note, num, pillGrey, pillRed, table, td, th } from '../ui';

// ── Expenses › Payments (owner 2026-11-02, "Standardized Document Table Layout"; menus.ts has the dated exception to batch 7 R5) ───────
// One row per payment, GET /acct-workspace/expense-payments (services/paymentsList.ts): the seven columns, with Payee for Customer and Total
// the amount paid. ⛔ READ-ONLY, BY THE OWNER'S RULING, FOR EVERY SEAT: no New, no Status dropdown although a payment's reversal is a door of
// its own, no write in the ⋯. A payment is recorded and reversed on its bill (PayablePayments), and Open shows that bill: a payment has no
// window of its own (sourceDoc.tsx NO_SCREEN_KEY), so this is the window its related-documents popover opens too. A payment from before
// payment numbers began shows "—": no backfill. (Review 2026-11-03) The paperclip is the attachments card's window, its own file and its own rules: it writes no payment, so this ruling stands.
export function ExpensePaymentsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwExpensePaymentPage | null>(null);
  const [err, setErr] = useState('');
  const { openRecord, recordWindow } = useOpenRecord(ctx);
  const ls = useListSort(ctx, 'expenses.payments', { onChange: () => setPage(1) });
  const { sort: sortK, dir: sortD } = sortArgs(ls);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awExpensePayments(ctx.clientId, { q: qd, page, limit, sort: sortK, dir: sortD })
      .then((d: AwExpensePaymentPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, qd, page, limit, sortK, sortD]);   // eslint-disable-line react-hooks/exhaustive-deps
  const rows = data?.rows || [];
  const clipsNew = useAttachmentCounts(ctx.clientId, 'expense_payment', rows.filter(r => r.kind === 'expense_payment').map(r => r.id));
  const clipsOld = useAttachmentCounts(ctx.clientId, 'payable_payment', rows.filter(r => r.kind === 'payable_payment').map(r => r.id));
  const open = (r: AwExpensePaymentRow) => { if (r.opens) openRecord(r.opens.kind, r.opens.id, r.opens.docNo, r.opens.record); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_ep_search_ph')} aria-label={t('aw_ep_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t(qd ? 'aw_no_rows' : 'aw_ep_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <SortTh k="pay_no" s={ls} label={t('aw_col_no')} /><SortTh k="date" s={ls} label={t('aw_col_date')} /><SortTh k="payee" s={ls} label={t('aw_col_payee')} />
                <SortTh k="total" s={ls} align="right" label={t('aw_col_total')} /><th style={th}>{t('aw_col_currency')}</th>
                <SortTh k="status" s={ls} label={t('aw_col_status')} /><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const gone = !!r.voided;
                const strike: React.CSSProperties = gone ? STRUCK : {};
                const when = r.voided?.at ? fmtDateTime(r.voided.at) : '—';
                return (
                  <tr key={r.id} data-payment-row={r.id} onClick={() => open(r)} style={{ cursor: r.opens ? 'pointer' : undefined }}>
                    {/* The paperclip beside the number, with the related-documents icon: the Actions cell holds one ⋯ and nothing else ("Standardized Document Table Layout"). */}
                    <NoCell strike={gone} no={r.pay_no || <NoNumber title={t('aw_rel_no_number')} />} sub={r.reference || undefined} rel={{ ctx, kind: r.kind, id: r.id, docNo: r.pay_no, count: r.linked_count }}
                      tail={<AttachmentsBtn clientId={ctx.clientId} kind={r.kind} id={r.id} docNo={r.pay_no || r.reference} count={(r.kind === 'expense_payment' ? clipsNew : clipsOld).counts[r.id]} onClosed={(r.kind === 'expense_payment' ? clipsNew : clipsOld).refresh} />} />
                    <td style={{ ...td, whiteSpace: 'nowrap', ...strike }}>{r.date ? dmy(r.date) : '—'}</td>
                    <td style={{ ...td, ...strike }}>{r.payees > 1 ? t('aw_ep_payee_more', { name: r.payee, n: r.payees - 1 }) : (r.payee || '—')}</td>
                    <td style={{ ...num, ...strike }}>{amt(r.total)}</td>
                    <td style={{ ...td, ...strike }}>{r.currency}</td>
                    {/* Recorded and Reversed, the popover's words for these two states (relatedDocs.tsx STATUS_KEY). ⛔ Never "Posted": a live payment
                        may have no settlement entry yet (paymentsList.ts). Plain: this list changes nothing. */}
                    <StatusCell word={t(gone ? 'aw_rel_st_reversed' : 'aw_rel_st_recorded')} tone={gone ? pillRed : pillGrey} value={r.state}>
                      {r.wht > 0.005 && <FigureLine name="wht" label={t('aw_pay_wht')} value={r.wht} />}
                      {r.voided && <SmallLine name="voided">{r.voided.reason ? t('aw_epay_reversed_by', { by: r.voided.by || '—', when, reason: r.voided.reason }) : t('aw_epay_taken_off_by', { by: r.voided.by || '—', when })}</SmallLine>}
                    </StatusCell>
                    <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <RowMenu items={() => [{ key: 'open', icon: 'ti-arrows-maximize', label: t('aw_er_open'), title: t('aw_rel_payment_title'), onPick: () => open(r), withheld: r.opens ? undefined : t('aw_rel_no_window') }]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_ep_note')}</div>
      {recordWindow}
    </div>
  );
}
